import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../context/JourneyContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, ArrowRight, Quote, Users, School, Star, Heart, Sparkles, Award, UserCheck } from 'lucide-react';

const TESTIMONIALS = [
  {
    quote: "Let's Siva is mana enhancing for all students and teachers… It is without hesitation that I recommend Let's Siva to be a part of any school programme.",
    source: "School Principal"
  },
  {
    quote: "Students experienced significant growth in their confidence and perseverance.",
    source: "Deputy Principal"
  },
  {
    quote: "The hype levels in the school during the 5 weeks are next level.",
    source: "School Leader"
  }
];

const STATS = [
  { icon: School, value: '30+', label: 'Schools Across Aotearoa' },
  { icon: Users, value: '16,000+', label: 'Students Reached' },
];

const OUTCOMES = [
  { icon: Star, label: 'Confidence Development' },
  { icon: Users, label: 'Whole-School Unity' },
  { icon: Award, label: 'Leadership Growth' },
  { icon: UserCheck, label: 'Teacher Capability' },
  { icon: Heart, label: 'Community Connection' },
];

const Proof = () => {
  const navigate = useNavigate();
  const { trackPageView } = useJourney();

  React.useEffect(() => {
    trackPageView('portal_proof');
  }, [trackPageView]);

  return (
    <div 
      data-testid="proof-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-20 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Navigation */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/portal/your-need')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <div className="h-1 w-8 rounded-full bg-stone-700" />
          <span className="ml-4 text-sm text-stone-400">Step 2 of 4</span>
        </div>

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h1 
            data-testid="proof-title"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            Impact & Outcomes
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-xl mx-auto">
            Built and sustained through long-term school partnerships and community support.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-10">
          {STATS.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                data-testid={`stat-${index}`}
                className="bg-stone-900/50 border border-stone-800 p-6 text-center rounded-2xl animate-fade-in-up opacity-0"
                style={{ animationDelay: `${0.1 + index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <Icon className="w-8 h-8 text-primary mx-auto mb-3" />
                <p className="text-3xl sm:text-4xl font-heading font-bold text-white">
                  {stat.value}
                </p>
                <p className="text-sm text-stone-400">
                  {stat.label}
                </p>
              </Card>
            );
          })}
        </div>

        {/* Outcome categories */}
        <div className="mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4 text-center">
            Clear Outcome Categories
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {OUTCOMES.map((outcome, index) => {
              const Icon = outcome.icon;
              return (
                <div 
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-stone-900/50 border border-stone-800"
                >
                  <Icon className="w-4 h-4 text-accent" />
                  <span className="text-sm text-stone-300">{outcome.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Leadership Voices */}
        <div className="mb-10 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-6 text-center">
            Selected Leadership Voices
          </h3>
          <div className="space-y-4">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card
                key={index}
                data-testid={`testimonial-${index}`}
                className="bg-stone-900/50 border border-stone-800 p-6 rounded-2xl animate-fade-in-up opacity-0"
                style={{ animationDelay: `${0.4 + index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <Quote className="w-8 h-8 text-stone-700 mb-3" />
                <p className="text-stone-300 font-accent text-lg mb-3">
                  "{testimonial.quote}"
                </p>
                <p className="text-stone-500 text-sm">— {testimonial.source}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Multi-year partnerships note */}
        <div className="bg-stone-900/80 border border-accent/30 rounded-2xl p-6 text-center mb-10 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
          <Sparkles className="w-8 h-8 text-accent mx-auto mb-3" />
          <p className="text-lg font-heading font-bold text-white mb-1">
            Multi-year school partnerships
          </p>
          <p className="text-stone-400 text-sm">
            Leadership-endorsed partnerships across primary, intermediate, and high school settings.
          </p>
          <p className="text-stone-500 text-xs mt-3">
            Full survey insights coming soon.
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            data-testid="continue-btn"
            onClick={() => navigate('/portal/options')}
            size="lg"
            className="rounded-full px-10 py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-105"
          >
            See your options
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Proof;
